import { ApiService } from '@sparklayer-io/spark-dash-core/types/api';
import { ApiResponseError, isApiErrorStatus } from '@sparklayer-io/spark-dash-core/types/errors';
import type { Customer, CustomerLookupBy, CustomerLookupResult } from '../types';

export type CustomersSyncApiSettings = {
  useCustomersAPI: boolean;
  dualWriteToCustomersAPI: boolean;
};

const PLATFORM_SOURCE: CustomerLookupBy = 'platform';
const SPARKLAYER_SOURCE: CustomerLookupBy = 'sparklayer';

function customersBaseUrl(useCustomersAPI: boolean): string {
  return useCustomersAPI ? '/v2/customers' : '/v1/customers';
}

function assertIdentifierMatch(
  customer: Customer,
  identifier: string,
  field: 'id' | 'external_id',
): Customer {
  if (customer[field] !== identifier) {
    throw new ApiResponseError(404, customer);
  }
  return customer;
}

export interface CustomersSyncApiContract {
  lookup(identifier: string, settings: CustomersSyncApiSettings): Promise<CustomerLookupResult>;
  updateRole(
    lookupBy: CustomerLookupBy,
    identifier: string,
    payload: { role: string },
    settings: CustomersSyncApiSettings,
  ): Promise<Customer>;
  updateGroups(
    lookupBy: CustomerLookupBy,
    identifier: string,
    payload: { sales_agent_groups: string[] },
    settings: CustomersSyncApiSettings,
  ): Promise<Customer>;
  salesAgentGroups(settings: CustomersSyncApiSettings): Promise<string[]>;
}

declare module '@sparklayer-io/spark-dash-core/context/page-context' {
  interface Apis {
    // Optional because `pages` builds its own apis without this one. Use `useCustomersSyncApi()`.
    customersSync?: CustomersSyncApiContract;
  }
}

export class CustomersSyncApi extends ApiService implements CustomersSyncApiContract {
  public async lookup(
    identifier: string,
    { useCustomersAPI }: CustomersSyncApiSettings,
  ): Promise<CustomerLookupResult> {
    const baseUrl = customersBaseUrl(useCustomersAPI);

    try {
      const customer = await this.api.proxy<Customer>({
        method: 'GET',
        path: `${baseUrl}/{lookupBy}/{identifier}`,
        params: { lookupBy: PLATFORM_SOURCE, identifier },
      });
      return {
        customer: assertIdentifierMatch(customer, identifier, 'external_id'),
        identifier,
        lookupBy: PLATFORM_SOURCE,
      };
    } catch (err) {
      if (!isApiErrorStatus(err, 404)) {
        throw err;
      }
    }

    if (!useCustomersAPI) {
      const customer = await this.api.proxy<Customer>({
        method: 'GET',
        path: `${baseUrl}/{id}`,
        params: { id: identifier },
      });
      return {
        customer: assertIdentifierMatch(customer, identifier, 'id'),
        identifier,
        lookupBy: SPARKLAYER_SOURCE,
      };
    }

    const customer = await this.api.proxy<Customer>({
      method: 'GET',
      path: `${baseUrl}/{lookupBy}/{identifier}`,
      params: { lookupBy: SPARKLAYER_SOURCE, identifier },
    });
    return {
      customer: assertIdentifierMatch(customer, identifier, 'id'),
      identifier,
      lookupBy: SPARKLAYER_SOURCE,
    };
  }

  public async updateRole(
    lookupBy: CustomerLookupBy,
    identifier: string,
    payload: { role: string },
    { useCustomersAPI, dualWriteToCustomersAPI }: CustomersSyncApiSettings,
  ): Promise<Customer> {
    const paths = dualWriteToCustomersAPI
      ? ['/v1/customers/{lookupBy}/{identifier}', '/v2/customers/{lookupBy}/{identifier}']
      : [`${customersBaseUrl(useCustomersAPI)}/{lookupBy}/{identifier}`];

    let result: Customer | undefined;
    for (const path of paths) {
      result = await this.api.proxy<Customer>({
        method: 'PATCH',
        path,
        params: { lookupBy, identifier },
        payload,
      });
    }

    // Unreachable in practice (`paths` always has at least one entry), but keeps the return type honest.
    if (!result) {
      throw new Error('updateRole: no endpoint was written to');
    }

    return result;
  }

  public updateGroups(
    lookupBy: CustomerLookupBy,
    identifier: string,
    payload: { sales_agent_groups: string[] },
    { useCustomersAPI }: CustomersSyncApiSettings,
  ): Promise<Customer> {
    return this.api.proxy<Customer>({
      method: 'PATCH',
      path: `${customersBaseUrl(useCustomersAPI)}/{lookupBy}/{identifier}`,
      params: { lookupBy, identifier },
      payload,
    });
  }

  public salesAgentGroups({ useCustomersAPI }: CustomersSyncApiSettings): Promise<string[]> {
    return this.api.proxy<string[]>({
      method: 'GET',
      path: useCustomersAPI ? '/v2/sales-agent-groups' : '/internal/v1/sales-agent-groups',
    });
  }
}
